import { api, ApiError } from './api';
import {
  AdminFeatures,
  AuditActor,
  AuditCatalogue,
  AuditDashboard,
  AuditEntry,
  AuditEntryDetail,
  AuditFilter,
  CursorPage,
  DashboardWindowName,
} from '../types/audit';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

/** The audit list page size (design.md §3: default 50, max 100). */
export const AUDIT_PAGE_SIZE = 50;

/** Builds the shared list/export query. `action` repeats, one per code (the API accepts both forms). */
export function auditQuery(filter: AuditFilter, extra: Record<string, string | undefined> = {}): string {
  const q = new URLSearchParams();
  if (filter.from) q.set('from', filter.from);
  if (filter.to) q.set('to', filter.to);
  if (filter.adminSubjectId) q.set('adminSubjectId', filter.adminSubjectId);
  (filter.actions ?? []).forEach((a) => q.append('action', a));
  if (filter.entityType) q.set('entityType', filter.entityType);
  if (filter.garageId) q.set('garageId', filter.garageId);
  if (filter.q) q.set('q', filter.q);
  Object.entries(extra).forEach(([k, v]) => { if (v) q.set(k, v); });
  const s = q.toString();
  return s ? `?${s}` : '';
}

function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8) return decodeURIComponent(utf8[1]);
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1] : null;
}

/**
 * Copy of `financeApi.downloadCsv` (module-private there): the gateway needs the Bearer token, so
 * fetch → blob → object URL → synthetic anchor. A refusal is JSON (`{code, message}`), surfaced as
 * an ApiError carrying the code so the page can map 422 EXPORT_TOO_LARGE to its inline hint.
 */
async function downloadCsv(url: string, token: string): Promise<string> {
  const response = await fetch(`${API_BASE}${url}`, {
    headers: { Authorization: `Bearer ${token}`, Accept: 'text/csv, application/json' },
  });
  if (!response.ok) {
    const body: { message?: unknown; code?: unknown } = await response.json().catch(() => ({}));
    const message = typeof body.message === 'string' && body.message ? body.message : `HTTP ${response.status}`;
    throw new ApiError(message, response.status, typeof body.code === 'string' ? body.code : undefined);
  }
  const blob = await response.blob();
  const filename = filenameFromDisposition(response.headers.get('Content-Disposition')) ?? 'garagat-audit.csv';
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  return filename;
}

export const auditApi = {
  list: (filter: AuditFilter, cursor: string | undefined, token: string, size = AUDIT_PAGE_SIZE) =>
    api.get<CursorPage<AuditEntry>>(`/api/admin/audit${auditQuery(filter, { cursor, size: String(size) })}`, token),

  get: (id: string, token: string) =>
    api.get<AuditEntryDetail>(`/api/admin/audit/${encodeURIComponent(id)}`, token),

  catalogue: (token: string) => api.get<AuditCatalogue>('/api/admin/audit/catalogue', token),

  actors: (token: string) => api.get<AuditActor[]>('/api/admin/audit/actors', token),

  exportCsv: (filter: AuditFilter, token: string) =>
    downloadCsv(`/api/admin/audit/export.csv${auditQuery(filter)}`, token),

  dashboard: (window: DashboardWindowName, token: string, signal: AbortSignal) =>
    api.getAbortable<AuditDashboard>(`/api/admin/audit/dashboard?window=${window}`, token, signal),

  features: (token: string, signal?: AbortSignal) => (signal
    ? api.getAbortable<AdminFeatures>('/api/admin/audit/features', token, signal)
    : api.get<AdminFeatures>('/api/admin/audit/features', token)),
};
